import TargetsPage from "./components/dashboard/customerTargets";

import { ChakraProvider } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import "./App.css";
import { ChartOfAccounts } from "./components/account/chartOfAccounts";
import { ClientDetail } from "./components/client/clientDetail";

import { ClientList } from "./components/client/clientList";
import { ClientQuickbooksLink } from "./components/client/clientQuickbooksLink";
import { ForgotPassword } from "./components/forgotPassword";
import { Logout } from "./components/logout";
import Navigation from "./components/navigation";
import { ResetPassword } from "./components/resetPassword";
import ThemeView from "./components/themeView";
import { UserManagement } from "./components/user/userManagement";
import { VendorsList } from "./components/vendors/vendorsList";

import { fetchCurrentProfile } from "./api/profile";
import AccountManagement from "./components/admin/accountManagement";
import { AdminChat } from "./components/admin/adminChat";
import { BulkQuickbooksSync } from "./components/admin/bulkQuickbooksSync";
import QuickBooksRawData from "./components/admin/QuickBooksRawData";
import QuickBooksSyncHistory from "./components/admin/quickBooksSyncHistory";
import SyncJobDetails from "./components/admin/SyncJobDetails";
import { ToolDetail } from "./components/adminTools/toolDetail";
import { ResourceVault } from "./components/businessResources/resourceVault";
import BankActivity from "./components/clientDashboard/BankActivity";
import PerformanceDescriptorPage from "./components/clientDashboard/performance/performanceDescriptorPage";
import PerformanceOverview from "./components/clientDashboard/performance/performanceOverview";
import ScenarioDetail from "./components/clientDashboard/scenario_planner/scenario_detail";
import ScenarioList from "./components/clientDashboard/scenario_planner/scenario_list";
import ScenarioPlanner from "./components/clientDashboard/scenario_planner/scenario_planner";
import UnderConstructionPage from "./components/clientDashboard/UnderConstructionPage";
import AdvancedMetrics from "./components/dashboard/advancedMetrics";
import { GrowthPlan } from "./components/dashboard/growthPlan";
import IndustryInsightsSelection from "./components/dashboard/industryInsightsSelection";
import PerformanceReview from "./components/dashboard/performanceReview";
import { ReportNav } from "./components/dashboard/reports/reportNav";
import VendorAnalytics from "./components/dashboard/vendorAnalytics";
import GoogleDriveAuth from "./components/google/GoogleDriveAuth";
import { OperationalFlow } from "./components/operationFlow/lucroFlow";
import { ProfileSettings } from "./components/profileSettings/profileSettingsPage";
import QualityControlReviews from "./components/quality/qualityControlReviews";
import ReviewDetails from "./components/quality/ReviewDetails";
import { SignUpValidation } from "./components/signUpValidation";
import { TutorialVault } from "./components/tutorial/tutorialListing";
import { WelcomeForm } from "./components/welcome";
import { WelcomeLegacyForm } from "./components/welcomeLegacy";
import BankSetupOnly from "./components/welcomeSetup/plaidSetupOnly";
import SetupPage from "./components/welcomeSetup/setupPage";
import SetupPrompt from "./components/welcomeSetup/setupPrompt";
import ContextWrapper from "./ContextWrapper";
import ManagementScreenMvp from "./features/mvp/management/managementScreen";
import ManagementScreenPlatform from "./features/platform/management/managementScreen";
import theme from "./theme";
import ProfileOverviewPage from "./components/clientDashboard/ProfileOverviewPage";
import ReportsPage from "./components/clientDashboard/ReportsPage";
import VaultPage from "./components/clientDashboard/VaultPage";
import CustomerSettings from "./features/mvp/management/settings/customerSettingsPage";
import UserSettings from "./features/mvp/management/settings/userSettingsPage";
import { getNewExperienceEnabled } from "./utils/getNewExperienceEnabled";
import TargetAllocation from "./components/clientDashboard/TargetAllocation";
import FeaturePreview from "./components/clientDashboard/previewTemplate";

function App() {
  const [isAuth, setIsAuth] = useState(false);
  const [profile, setProfile] = useState(null);
  const getStoredId = () => localStorage.getItem("customerOnboardingId");

  const [storedId, setStoredId] = useState(getStoredId());
  const newExperienceEnabled = useMemo(() => {
    return getNewExperienceEnabled(profile);
  }, [profile]);

  const clientOnboardingCompleted = useMemo(() => {
    if (!profile) return false;

    const customers = Array.isArray(profile.customers) ? profile.customers : [];
    const nonDemoCustomers = customers.filter((c) => !c?.is_demo);
    // If the user only has demo customers, always show onboarding on login.
    if (nonDemoCustomers.length === 0) {
      try {
        return sessionStorage.getItem("demo_onboarding_bypass") === "true";
      } catch (error) {
        return false;
      }
    }

    if (
      profile.completed_first_time_setup ||
      profile.skipped_first_time_setup
    ) {
      return true;
    }

    return nonDemoCustomers.some(
      (c) =>
        c?.customer_type !== "trial" ||
        c?.completed_first_time_setup === true ||
        c?.skipped_first_time_setup === true,
    );
  }, [profile]);

  const welcomeElement = newExperienceEnabled ? (
    <WelcomeForm />
  ) : (
    <WelcomeLegacyForm />
  );

  useEffect(() => {
    const handleOnboardingChange = () => {
      const newId = localStorage.getItem("customerOnboardingId");
      setStoredId(newId);
    };

    window.addEventListener(
      "customerOnboardingId-changed",
      handleOnboardingChange,
    );

    return () => {
      window.removeEventListener(
        "customerOnboardingId-changed",
        handleOnboardingChange,
      );
    };
  }, []);

  useEffect(() => {
    const handleStorage = () => {
      // Check access_token from either localStorage or sessionStorage
      const accessToken =
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("access_token");

      if (accessToken !== "null" && accessToken !== null) {
        setIsAuth(true);

        fetchCurrentProfile().then(([currentProfile]) => {
          if (!currentProfile.is_active) {
            setIsAuth(false);
          } else {
            // TODO: remove this temporary hack we're using to limit access to new accounting things
            currentProfile.hasAccountingAccess = currentProfile.is_admin;
            currentProfile.isAdmin = currentProfile.type === "admin";
            setProfile(currentProfile);
          }
        });
      } else {
        setIsAuth(false);
      }
    };

    handleStorage();

    window.addEventListener("storage", handleStorage);

    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return (
    <ChakraProvider theme={theme}>
      <ContextWrapper profile={profile}>
        <BrowserRouter>
          {!(isAuth && profile?.type !== "client" && newExperienceEnabled) && (
            <LoadNavigation
              isAuth={isAuth}
              profile={profile}
              setProfile={setProfile}
            />
          )}
          {isAuth && profile?.type !== "client" && newExperienceEnabled && (
            <Routes>
              <Route
                path="/"
                element={<Navigate to="/management/accounts" replace />}
              />
              <Route
                path="/management"
                element={<Navigate to="/management/accounts" replace />}
              />
              <Route
                path="/clients"
                element={<Navigate to="/management/accounts" replace />}
              />
              <Route
                path="/account-management"
                element={<Navigate to="/management/accounts" replace />}
              />
              <Route
                path="/users"
                element={<Navigate to="/management/users" replace />}
              />
              <Route
                path="/client/:customer_id"
                element={<Navigate to="./dashboard/profile-overview" replace />}
              />
              <Route
                path="/client/:customer_id/dashboard/profile-overview"
                element={<ProfileOverviewPage title="Profile Overview" />}
              />
              <Route
                path="/client/:customer_id/dashboard/bank-activity"
                element={<BankActivity />}
              />
              <Route
                path="/client/:customer_id/dashboard/saas-target"
                element={<TargetAllocation />}
              />
              <Route
                path="/client/:customer_id/dashboard/performance-overview"
                element={<PerformanceOverview />}
              />
              <Route
                path="/client/:customer_id/dashboard/performance/:descriptor"
                element={<PerformanceDescriptorPage />}
              />
              <Route
                path="/client/:customer_id/performance/:descriptor"
                element={<PerformanceDescriptorPage />}
              />
              <Route
                path="/client/:customer_id/performance-overview"
                element={<PerformanceOverview />}
              />
              <Route
                path="/client/:customer_id/dashboard/reports"
                element={<ReportsPage />}
              />
              <Route
                path="/client/:customer_id/dashboard/vault"
                element={<VaultPage title="The Vault" />}
              />
              <Route
                path="/client/:customer_id/dashboard/settings"
                element={<CustomerSettings />}
              />
              {(profile?.type === "admin" ||
                profile?.type === "accountant") && (
                <Route
                  path="/management/:section"
                  element={<ManagementScreenMvp experience="mvp" />}
                />
              )}
              <Route path="/login" element={welcomeElement} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/forgotPassword" element={<ForgotPassword />} />
              <Route
                path="/reset"
                element={<ResetPassword register={false} />}
              />
              <Route
                path="/register"
                element={<ResetPassword register={true} />}
              />
              <Route path="/profile-settings" element={<UserSettings />} />
              <Route path="/themeView" element={<ThemeView />} />
              <Route
                path="*"
                element={<Navigate to="/management/accounts" replace />}
              />
            </Routes>
          )}

          {isAuth && profile?.type !== "client" && !newExperienceEnabled && (
            <Routes>
              <Route
                path="/"
                element={
                  profile?.type === "accountant" || profile?.type === "manager" ? (
                    <ClientList />
                  ) : (
                    profile?.type === "admin" ? (
                      <Navigate to="/management/accounts" replace />
                    ) : (
                      <> </>
                    )
                  )
                }
              />
              <Route path="/chat" element={<AdminChat />} />
              <Route path="/chat/:conversationId" element={<AdminChat />} />
              {profile?.type === "admin" && (
                <Route
                  path="/client/:customer_id/chat/:conversationId"
                  element={<ClientDetail />}
                />
              )}
              <Route
                path="/client/:customer_id/bank-activity"
                element={<BankActivity />}
              />
              <Route
                path="/client/:customer_id/:tabSlug?"
                element={<ClientDetail />}
              />
              {(profile?.type === "admin" ||
                profile?.type === "accountant") && (
                <Route path="/clients" element={<ClientList />} />
              )}
              {profile?.type === "admin" && (
                <Route
                  path="/bulk-quickbooks-sync"
                  element={<BulkQuickbooksSync />}
                />
              )}

              {profile?.type === "admin" && (
                <Route
                  path="/account-management"
                  element={<AccountManagement />}
                />
              )}
              {profile?.type === "admin" && (
                <Route
                  path="/management/:section"
                  element={<ManagementScreenPlatform />}
                />
              )}
              {profile?.type === "admin" && (
                <Route
                  path="/quickbooks-sync-history"
                  element={<QuickBooksSyncHistory />}
                />
              )}
              {profile?.type === "admin" && (
                <Route
                  path="/quickbooks-raw-data/:customerId"
                  element={<QuickBooksRawData />}
                />
              )}
              {profile?.type === "admin" && (
                <Route path="/sync-jobs/:jobId" element={<SyncJobDetails />} />
              )}
              {profile?.isAdmin && (
                <Route path="/vendors" element={<VendorsList />} />
              )}
              {profile?.isAdmin && (
                <Route
                  path="/chart-of-accounts-template"
                  element={<ChartOfAccounts customer_id={""} template={true} />}
                />
              )}
              {profile?.isAdmin && (
                <>
                  <Route path="/users" element={<UserManagement />} />
                  <Route path="/admin-tools" element={<ToolDetail />} />
                </>
              )}
              {profile?.type === "admin" || profile?.type === "accountant" ? (
                <Route
                  path="/quality-control-reviews"
                  element={
                    <QualityControlReviews
                      userType={profile?.type.toUpperCase()}
                    />
                  }
                />
              ) : null}
              {profile?.type === "admin" || profile?.type === "accountant" ? (
                <Route
                  path="/quality-control-reviews/:id"
                  element={<ReviewDetails />}
                />
              ) : null}
              <Route path="/login" element={welcomeElement} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/forgotPassword" element={<ForgotPassword />} />
              <Route
                path="/reset"
                element={<ResetPassword register={false} />}
              />
              <Route
                path="/register"
                element={<ResetPassword register={true} />}
              />
              <Route path="/qblink" element={<ClientQuickbooksLink />} />
              <Route path="/themeView" element={<ThemeView />} />
              <Route path="/googledriveauth" element={<GoogleDriveAuth />} />
              <Route path="/growth-plan" element={<GrowthPlan />} />
              <Route
                path="/performance-review"
                element={<PerformanceReview></PerformanceReview>}
              />
              <Route
                path="/industry-insights"
                element={<IndustryInsightsSelection />}
              />
              <Route path="/reports" element={<ReportNav />} />
              <Route path="/vault" element={<ResourceVault />} />
              <Route path="/lucro-flow" element={<OperationalFlow />} />
              <Route path="/tutorial" element={<TutorialVault />} />
              <Route
                path="/performance-overview"
                element={<PerformanceOverview />}
              />

              <Route
                path="/performance-review/metric"
                element={<PerformanceReview></PerformanceReview>}
              />
              <Route
                path="/performance-review/detail"
                element={<PerformanceReview></PerformanceReview>}
              />
              <Route
                path="/industry-insights/top-one"
                element={<IndustryInsightsSelection />}
              />
              <Route
                path="/industry-insights/top-ten"
                element={<IndustryInsightsSelection />}
              />
              <Route
                path="/industry-insights/average"
                element={<IndustryInsightsSelection />}
              />

              <Route path="/vendor-analysis" element={<VendorAnalytics />} />
              <Route
                path="/client/:customer_id/dashboard/bank-activity"
                element={<BankActivity />}
              />
              <Route
                path="/client/:customer_id/dashboard/performance-overview"
                element={<PerformanceOverview />}
              />
              <Route
                path="/client/:customer_id/dashboard/performance/:descriptor"
                element={<PerformanceDescriptorPage />}
              />
              <Route
                path="/client/:customer_id/bank-activity"
                element={<BankActivity />}
              />
              <Route path="/bank-activity" element={<BankActivity />} />
              <Route
                path="/client/:customer_id/performance-overview"
                element={<PerformanceOverview />}
              />
              <Route
                path="/client/:customer_id/performance/:descriptor"
                element={<PerformanceDescriptorPage />}
              />
              <Route path="/reports/performance" element={<ReportNav />} />
              <Route path="/reports/profit-loss" element={<ReportNav />} />
              <Route path="/reports/balance-sheet" element={<ReportNav />} />
              <Route path="/reports/royalty" element={<ReportNav />} />

              <Route path="/lucro-metrics" element={<AdvancedMetrics />} />
              <Route
                path="/lucro-metrics/:type"
                element={<AdvancedMetrics />}
              />
              <Route
                path="/lucro-metrics/:type/:prevPage"
                element={<AdvancedMetrics />}
              />
              <Route path="/targets" element={<TargetsPage />} />
              <Route
                path="/preview-scenario-planner"
                element={
                  <FeaturePreview
                    title={"Scenario Planner"}
                    description={
                      "Test what if decisions and see the downstream impact on profit and cash."
                    }
                    imagePath={"/platformPreviewImages/scenarioPlanner.svg"}
                    isSaasExperience={false}
                  />
                }
              />

              <Route
                path="/preview-capacity-calculator"
                element={
                  <FeaturePreview
                    title={"Capacity Calculator"}
                    description={
                      "Determine the ideal capacity in your business to maximize income production and efficiency."
                    }
                    imagePath={"/platformPreviewImages/capacityCalculator.svg"}
                    isSaasExperience={false}
                  />
                }
              />
              <Route
                path="/preview-insights"
                element={
                  <FeaturePreview
                    title={"Lucro Insights"}
                    description={
                      "Compare your performance to industry performance using benchmarks that support strategic decisions."
                    }
                    imagePath={"/platformPreviewImages/lucroInsights.svg"}
                    isSaasExperience={false}
                  />
                }
              />
              <Route
                path="/preview-bank-activity"
                element={
                  <FeaturePreview
                    title={"Bank Activity"}
                    description={
                      "Review and finalize transaction categorization to ensure maximum accuracy before your data is analyzed."
                    }
                    imagePath={"/platformPreviewImages/bankActivity.svg"}
                    isSaasExperience={false}
                  />
                }
              />
            </Routes>
          )}
          {isAuth && profile?.type === "client" && !newExperienceEnabled && (
            <Routes>
              <Route path="/" element={<GrowthPlan />} />
              <Route path="/login" element={welcomeElement} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/forgotPassword" element={<ForgotPassword />} />
              <Route
                path="/reset"
                element={<ResetPassword register={false} />}
              />
              <Route
                path="/register"
                element={<ResetPassword register={true} />}
              />
              <Route path="/growth-plan" element={<GrowthPlan />} />
              <Route
                path="/performance-review"
                element={<PerformanceReview></PerformanceReview>}
              />
              <Route
                path="/industry-insights"
                element={<IndustryInsightsSelection />}
              />
              <Route path="/reports" element={<ReportNav />} />
              <Route path="/vault" element={<ResourceVault />} />
              <Route path="/lucro-flow" element={<OperationalFlow />} />
              <Route path="/tutorial" element={<TutorialVault />} />
              <Route
                path="/settings"
                element={<ProfileSettings setProfile={setProfile} />}
              />
              <Route
                path="/performance-review/metric"
                element={<PerformanceReview></PerformanceReview>}
              />
              <Route
                path="/performance-review/detail"
                element={<PerformanceReview></PerformanceReview>}
              />
              <Route
                path="/industry-insights/top-one"
                element={<IndustryInsightsSelection />}
              />
              <Route
                path="/industry-insights/top-ten"
                element={<IndustryInsightsSelection />}
              />
              <Route
                path="/industry-insights/average"
                element={<IndustryInsightsSelection />}
              />

              <Route path="/vendor-analysis" element={<VendorAnalytics />} />

              <Route
                path="/client/:customer_id/dashboard/performance-overview"
                element={<Navigate to="/" replace />}
              />
              <Route
                path="/client/:customer_id/dashboard/performance/:descriptor"
                element={<Navigate to="/" replace />}
              />
              <Route
                path="/client/:customer_id/performance-overview"
                element={<Navigate to="/" replace />}
              />
              <Route
                path="/client/:customer_id/performance/:descriptor"
                element={<Navigate to="/" replace />}
              />
              <Route path="/reports/performance" element={<ReportNav />} />
              <Route path="/reports/profit-loss" element={<ReportNav />} />
              <Route path="/reports/balance-sheet" element={<ReportNav />} />
              <Route path="/reports/royalty" element={<ReportNav />} />
              <Route
                path="/settings/profile"
                element={<ProfileSettings setProfile={setProfile} />}
              />
              <Route
                path="/settings/password"
                element={<ProfileSettings setProfile={setProfile} />}
              />
              <Route
                path="/settings/bank-management"
                element={<ProfileSettings setProfile={setProfile} />}
              />

              <Route path="/lucro-metrics" element={<AdvancedMetrics />} />
              <Route
                path="/lucro-metrics/:type"
                element={<AdvancedMetrics />}
              />
              <Route
                path="/lucro-metrics/:type/:prevPage"
                element={<AdvancedMetrics />}
              />
              {storedId && (
                <Route
                  path="/onboarding"
                  element={
                    <BankSetupOnly profile={profile} setProfile={setProfile} />
                  }
                />
              )}
              <Route path="/targets" element={<TargetsPage />} />
              <Route
                path="/preview-scenario-planner"
                element={
                  <FeaturePreview
                    title={"Scenario Planner"}
                    description={
                      "Test what if decisions and see the downstream impact on profit and cash."
                    }
                    imagePath={"/platformPreviewImages/scenarioPlanner.svg"}
                    isSaasExperience={false}
                  />
                }
              />

              <Route
                path="/preview-capacity-calculator"
                element={
                  <FeaturePreview
                    title={"Capacity Calculator"}
                    description={
                      "Determine the ideal capacity in your business to maximize income production and efficiency."
                    }
                    imagePath={"/platformPreviewImages/capacityCalculator.svg"}
                    isSaasExperience={false}
                  />
                }
              />
              <Route
                path="/preview-insights"
                element={
                  <FeaturePreview
                    title={"Lucro Insights"}
                    description={
                      "Compare your performance to industry performance using benchmarks that support strategic decisions."
                    }
                    imagePath={"/platformPreviewImages/lucroInsights.svg"}
                    isSaasExperience={false}
                  />
                }
              />
              <Route
                path="/preview-bank-activity"
                element={
                  <FeaturePreview
                    title={"Bank Activity"}
                    description={
                      "Review and finalize transaction categorization to ensure maximum accuracy before your data is analyzed."
                    }
                    imagePath={"/platformPreviewImages/bankActivity.svg"}
                    isSaasExperience={false}
                  />
                }
              />
            </Routes>
          )}

          {isAuth && profile?.type === "client" && newExperienceEnabled && (
            <Routes>
              <Route
                path="/"
                element={
                  !clientOnboardingCompleted ? (
                    <SetupPrompt profile={profile} setProfile={setProfile} />
                  ) : (
                    <Navigate to="/performance" replace />
                  )
                }
              />
              <Route path="/login" element={welcomeElement} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/forgotPassword" element={<ForgotPassword />} />
              <Route
                path="/reset"
                element={<ResetPassword register={false} />}
              />
              <Route
                path="/register"
                element={<ResetPassword register={true} />}
              />
              <Route path="/growth-plan" element={<GrowthPlan />} />
              <Route
                path="/industry-insights"
                element={<UnderConstructionPage title="Industry Insights" />}
              />
              <Route
                path="/performance-overview"
                element={<Navigate to="/performance" replace />}
              />
              <Route
                path="/performance"
                element={<PerformanceOverview setProfile={setProfile} />}
              />
              <Route
                path="/performance/:descriptor"
                element={<PerformanceDescriptorPage />}
              />
              <Route
                path="/client/:customer_id/dashboard/performance-overview"
                element={<Navigate to="/performance" replace />}
              />
              <Route
                path="/client/:customer_id/performance-overview"
                element={<Navigate to="/performance" replace />}
              />
              <Route
                path="/client/:customer_id/dashboard/scenario-planner"
                element={<Navigate to="/scenario-planner" replace />}
              />
              <Route
                path="/client/:customer_id/scenario-planner"
                element={<Navigate to="/scenario-planner" replace />}
              />
              <Route
                path="/client/:customer_id/dashboard/scenario-planner/create"
                element={<Navigate to="/scenario-planner/create" replace />}
              />
              <Route
                path="/client/:customer_id/scenario-planner/create"
                element={<Navigate to="/scenario-planner/create" replace />}
              />
              <Route
                path="/client/:customer_id/dashboard/performance/:descriptor"
                element={<RedirectToPerformanceDescriptor />}
              />
              <Route
                path="/client/:customer_id/performance/:descriptor"
                element={<RedirectToPerformanceDescriptor />}
              />
              <Route
                path="/reports"
                element={<ReportsPage title="Reports" />}
              />
              <Route path="/vault" element={<VaultPage title="Vault" />} />
              <Route path="/scenario-planner" element={<ScenarioPlanner />} />
              <Route
                path="/scenario-planner/create"
                element={<ScenarioList />}
              />
              <Route
                path="/scenario-planner/:scenarioId/edit"
                element={<ScenarioList />}
              />
              <Route
                path="/scenario-planner/:scenarioId"
                element={<ScenarioDetail />}
              />
              <Route
                path="/bank-activity"
                element={<BankActivity useNewLayout />}
              />
              <Route
                path="/preview-lucro-metrics"
                element={
                  <FeaturePreview
                    title={"Lucro Metrics"}
                    description={
                      "Track and analyze the key metrics that signal true business health, beyond basic income and expenses."
                    }
                    imagePath={"/platformPreviewImages/metrics.svg"}
                  />
                }
              />
              <Route
                path="/client/:customer_id/dashboard/preview-lucro-metrics"
                element={<Navigate to="/preview-lucro-metrics" replace />}
              />

              <Route
                path="/preview-industry-insights"
                element={
                  <FeaturePreview
                    title={"Industry Insights"}
                    description={
                      "Compare your performance to industry performance using benchmarks that support strategic decisions."
                    }
                    imagePath={"/platformPreviewImages/industryInsights.svg"}
                  />
                }
              />
              <Route
                path="/client/:customer_id/dashboard/preview-industry-insights"
                element={<Navigate to="/preview-industry-insights" replace />}
              />

              <Route
                path="/preview-bank-activity"
                element={
                  <FeaturePreview
                    title={"Bank Activity"}
                    description={
                      "Review and finalize transaction categorization to ensure maximum accuracy before your data is analyzed."
                    }
                    imagePath={"/platformPreviewImages/bankActivity.svg"}
                  />
                }
              />
              <Route
                path="/client/:customer_id/dashboard/preview-bank-activity"
                element={<Navigate to="/preview-bank-activity" replace />}
              />

              <Route
                path="/preview-targets"
                element={
                  <FeaturePreview
                    title={"Targets"}
                    description={
                      "Set financial targets tied to your metrics and track progress every month."
                    }
                    imagePath={"/platformPreviewImages/targets.svg"}
                  />
                }
              />
              <Route
                path="/client/:customer_id/dashboard/preview-targets"
                element={<Navigate to="/preview-targets" replace />}
              />

              <Route
                path="/preview-scenario-planner"
                element={
                  <FeaturePreview
                    title={"Scenario Planner"}
                    description={
                      "Test what if decisions and see the downstream impact on profit and cash."
                    }
                    imagePath={"/platformPreviewImages/scenarioPlanner.svg"}
                  />
                }
              />
              <Route
                path="/client/:customer_id/dashboard/preview-scenario-planner"
                element={<Navigate to="/preview-scenario-planner" replace />}
              />

              <Route
                path="/preview-capacity-calculator"
                element={
                  <FeaturePreview
                    title={"Capacity Calculator"}
                    description={
                      "Determine the ideal capacity in your business to maximize income production and efficiency."
                    }
                    imagePath={"/platformPreviewImages/capacityCalculator.svg"}
                  />
                }
              />
              <Route
                path="/client/:customer_id/dashboard/preview-capacity-calculator"
                element={<Navigate to="/preview-capacity-calculator" replace />}
              />

              <Route
                path="/preview-vendor-breakdown"
                element={
                  <FeaturePreview
                    title={"Vendor Breakdown"}
                    description={
                      "See vendor transactions in detail with period comparisons and outlier tracking for deeper clarity."
                    }
                    imagePath={"/platformPreviewImages/vendorBreakdown.svg"}
                  />
                }
              />
              <Route
                path="/client/:customer_id/dashboard/preview-vendor-breakdown"
                element={<Navigate to="/preview-vendor-breakdown" replace />}
              />

              <Route
                path="/preview-flow"
                element={<OperationalFlow drillDownEnabled={false} />}
              />
              <Route
                path="/client/:customer_id/dashboard/preview-flow"
                element={<Navigate to="/preview-flow" replace />}
              />

              <Route
                path="/preview-insights"
                element={
                  <FeaturePreview
                    title={"Lucro Insights"}
                    description={
                      "Compare your performance to industry performance using benchmarks that support strategic decisions."
                    }
                    imagePath={"/platformPreviewImages/lucroInsights.svg"}
                  />
                }
              />
              <Route
                path="/client/:customer_id/dashboard/preview-insights"
                element={<Navigate to="/preview-insights" replace />}
              />
              <Route path="/profile-settings" element={<UserSettings />} />
              <Route
                path="/reports/performance"
                element={<UnderConstructionPage title="Reports" />}
              />
              <Route
                path="/reports/profit-loss"
                element={<UnderConstructionPage title="Reports" />}
              />
              <Route
                path="/reports/balance-sheet"
                element={<UnderConstructionPage title="Reports" />}
              />
              <Route
                path="/reports/royalty"
                element={<UnderConstructionPage title="Reports" />}
              />
              <Route
                path="/industry-insights/top-one"
                element={<UnderConstructionPage title="Industry Insights" />}
              />
              <Route
                path="/industry-insights/top-ten"
                element={<UnderConstructionPage title="Industry Insights" />}
              />
              <Route
                path="/industry-insights/average"
                element={<UnderConstructionPage title="Industry Insights" />}
              />
              <Route path="/tutorial" element={<TutorialVault />} />
              <Route path="/settings" element={<CustomerSettings />} />
              <Route path="/settings/profile" element={<UserSettings />} />
              <Route
                path="/settings/password"
                element={<UserSettings initialTab="password-settings" />}
              />
              <Route
                path="/settings/bank-management"
                element={<CustomerSettings />}
              />
              <Route
                path="/welcome"
                element={
                  <SetupPrompt profile={profile} setProfile={setProfile} />
                }
              />
              <Route
                path="/setup"
                element={
                  <SetupPage profile={profile} setProfile={setProfile} />
                }
              />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
          {!isAuth && (
            <Routes>
              <Route path="/" element={welcomeElement} />
              <Route path="/login" element={welcomeElement} />
              <Route path="/signup" element={welcomeElement} />
              <Route path="/validate-email" element={<SignUpValidation />} />
              <Route path="/forgotPassword" element={<ForgotPassword />} />
              <Route
                path="/reset"
                element={<ResetPassword register={false} />}
              />
              <Route
                path="/register"
                element={<ResetPassword register={true} />}
              />
            </Routes>
          )}
        </BrowserRouter>
      </ContextWrapper>
    </ChakraProvider>
  );
}
export default App;

function RedirectToPerformanceDescriptor() {
  const { descriptor } = useParams();
  const safeDescriptor = descriptor ? String(descriptor) : "";
  return <Navigate to={`/performance/${safeDescriptor}`} replace />;
}

function LoadNavigation({ isAuth, profile }) {
  const location = useLocation();

  const hiddenNavRoutes = [
    "/growth-plan",
    "/performance-review",
    "/performance-review/metric",
    "/performance-review/detail",
    "/performance-overview",
    "/industry-insights",
    "/industry-insights/top-one",
    "/industry-insights/top-ten",
    "/industry-insights/average",
    "/reports",
    "/reports/performance",
    "/reports/profit-loss",
    "/reports/balance-sheet",
    "/reports/royalty",
    "/vault",
    "/lucro-flow",
    "/tutorial",
    "/vendor-analysis",
    "/lucro-metrics",
  ];

  const hideNavigation =
    hiddenNavRoutes.some((p) => location.pathname.startsWith(p)) ||
    location.pathname.startsWith("/lucro-metrics/") ||
    location.pathname.startsWith("/bank-activity") ||
    location.pathname.includes("/performance-overview");

  return (
    <>
      {!hideNavigation &&
        (profile?.type === "admin" || profile?.type === "accountant") &&
        isAuth && <Navigation isAdmin={profile?.isAdmin} />}
    </>
  );
}
